import styled from "styled-components";
import Backdrop from "components/global/Backdrop";
import SearchBar from "components/global/SearchBar";
import { useEffect, useMemo, useState } from "react";
import Spacer from "components/global/Spacer";
import { connect } from "react-redux";
import TutorialVideoModal from "components/global/TutorialVideoModal";
import { RocketLaunchIconOutline } from "icons/RocketLaunchIcon";
import { useHistory } from "react-router-dom";
import { options } from "components/global/GlobalSearch";
import { useClickOutside } from "webrix/hooks";
import CloseIcon from "icons/CloseIcon";
import NaraIcon from "assets/icons/nara-logo.png";
import ChatIconGrey from "assets/icons/chatIconGrey.svg";
import { generateHMAC } from "utils/utils";
import { socialIcons } from "constants/post";
import AirPlaneIcon from "assets/icons/airplaneIconOutline.svg";
import BulkPostingIcon from "icons/BulkPostingIcon";
import { PencilIconOutline } from "icons/PencilIcon";

const PromptWrapper = styled(Backdrop)`
  .modal {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 48px;
    border-radius: 16px;
    background: #fff;
    color: #07070c;
    max-width: 560px;
    margin: 0 auto;

    @media (max-width: 768px) {
      padding: 24px;
      margin: 0 12px;
    }
  }

  .resultContainer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
    max-height: 200px;
  }

  .resultItem {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
  }

  .searchBar {
    color: black;
    display: flex;
    height: 40px;
    padding: 10px 12px;
    align-items: center;
    gap: 6px;
  }

  .searchOption {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 15px;

    .searchOptionCard {
      display: flex;
      height: 96px;
      padding: 16px;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-start;
      border-radius: 8px;

      &.firstCard {
        background: #f0f0ff;
      }

      &.secondCard {
        background: #ebf6ff;
      }

      &.thirdCard {
        background: #e6f2f2;
      }
    }
  }
`;

const welcomeData = [
  {
    icon: AirPlaneIcon,
    text: "Create post",
    url: "/dashboard/posts/calendar-view?post_action=create_post",
    tutorial: {
      type: "Create caption",
      desktop_video: "https://player.vimeo.com/video/1026946764",
    },
  },
  {
    iconComponent: BulkPostingIcon,
    text: "Bulk posting",
    url: "/dashboard/brands",
    tutorial: {
      type: "Connect Social Media Accounts",
      desktop_video: "https://player.vimeo.com/video/1026947607",
    },
  },
  {
    iconComponent: PencilIconOutline,
    text: "Create draft",
    url: "/dashboard/posts/drafts",
    tutorial: {
      type: "Create post",
      desktop_video: "https://player.vimeo.com/video/947314013",
    },
  },
  {
    iconComponent: RocketLaunchIconOutline,
    text: "Quick post",
    url: "/dashboard/posts/calendar-view?post_action=create_post",
  },
];

const WelcomeSearchPrompt = ({ user }) => {
  const [showModal, setShowModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showTutorial, setShowTutorial] = useState(false);
  const history = useHistory();

  const handlePlayTutorial = (video) => {
    setShowTutorial(video);
  };

  const handleCardClick = (url) => {
    localStorage.setItem("showWelcomePrompt", "false");
    setShowModal(false);
    history.push(url);
  };

  const handleMouseDownCapture = useClickOutside(() => {
    setShowModal(false);
    localStorage.setItem("showWelcomePrompt", "false");
  });

  useEffect(() => {
    const welcomePrompt = localStorage.getItem("showWelcomePrompt");
    const verifyEmailPrompt = localStorage.getItem("showVerifyEmailPrompt");

    if (
      (user?.email_verified_at || verifyEmailPrompt) &&
      (!welcomePrompt || welcomePrompt === "true")
    ) {
      setShowModal(true);
    }
  }, []);

  const filteredData = useMemo(() => {
    if (!searchValue) return [];

    const matches = options.filter((option) =>
      option.keywords.some((keyword) =>
        keyword.toLowerCase().includes(searchValue)
      )
    );

    return matches.reduce((acc, option) => {
      if (!acc[option.group]) acc[option.group] = [];
      acc[option.group].push(option);
      return acc;
    }, {});
  }, [searchValue, options]);

  window.intercomSettings = {
    api_base: "https://api-iam.intercom.io",
    app_id: "vu0ufjy6",
    custom_launcher_selector: ".support-tour",
    hide_default_launcher: true,
  };

  useEffect(() => {
    const hmac = generateHMAC(user?.email, process.env.REACT_APP_INTERCOM_KEY);

    window.Intercom("boot", {
      app_id: "vu0ufjy6",
      name: user?.full_name,
      email: JSON.stringify(user?.email),
      user_hash: hmac,
    });
  }, [user]);

  if (!showModal) {
    return null;
  }

  return (
    <PromptWrapper className={`flexRow alignCenter justifyCenter show`}>
      <div className="modal" onMouseDownCapture={handleMouseDownCapture}>
        <div className="flexRow justifyEnd alignCenter">
          <CloseIcon
            className="cursorPointer"
            onClick={() => {
              setShowModal(false);
              localStorage.setItem("showWelcomePrompt", "true");
            }}
            color="#000"
          />
        </div>
        <div className="textCenter">
          <p className="colorGrey6"> Hello {user?.first_name}</p>
          <h4 className="textNoWrap">What would you like to do today?</h4>
        </div>
        <SearchBar
          placeholder="Search for features, AI tools, etc"
          keyword={searchValue}
          setKeyword={setSearchValue}
          handleSearch={() => setSearchValue(searchValue)}
          className="searchBar"
        />
        <div className="searchOption">
          <div
            className="searchOptionCard cursorPointer firstCard"
            onClick={() => handleCardClick("/dashboard/nara")}
          >
            <img src={NaraIcon} alt="" width="21px" height="21px" />
            <p className="l3">Chat&nbsp;with Nara</p>
          </div>
          <div
            className="searchOptionCard cursorPointer secondCard"
            onClick={() => handleCardClick("/dashboard/posts/calendar-view?post_action=create_post")}
          >
            <div>
              <RocketLaunchIconOutline color="#1D31A5" size={21} />
            </div>
            <p className="l3">Get&nbsp;quick post</p>
          </div>
          <div
            className="searchOptionCard cursorPointer thirdCard"
            onClick={() => handleCardClick("/dashboard/brands")}
          >
            <img src={socialIcons.grey["facebook"]} alt="" />
            <p className="l3">Connect account</p>
          </div>
        </div>
        <div className="resultContainer">
          {!searchValue ? (
            <>
              {welcomeData.map((item, index) => (
                <div
                  className="resultItem"
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(item?.url ?? item?.link);
                  }}
                >
                  <div className="flexRow alignCenter">
                    {item.icon ? (
                      <img src={item?.icon} alt="icon" />
                    ) : item?.iconComponent ? (
                      <item.iconComponent color="#9A9AAF" size={16} />
                    ) : null}
                    <Spacer x={1} />
                    <p>{item?.text}</p>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              {Object.entries(filteredData).map(([group, options], index) => (
                <div key={index}>
                  <p className="l3 semiBold colorGrey6">{group}</p>
                  {options.map((option, index) => (
                    <div
                      className="resultItem"
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCardClick(option.link);
                      }}
                    >
                      <div className="flexRow alignCenter">
                        {option.icon ? (
                          <img
                            src={option?.icon}
                            alt="icon"
                            style={{ maxWidth: "21px" }}
                          />
                        ) : option?.iconComponent ? (
                          <option.iconComponent color="#9A9AAF" size={16} />
                        ) : null}
                        <Spacer x={1} />
                        <p>{option?.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
        <div className="flexRow alignCenter justifyCenter">
          <img src={ChatIconGrey} alt="" />
          <Spacer x={0.5} />
          <p className="textCenter l4 colorBodyText textNoWrap">
            Can't find what you're looking for,{" "}
            <span className="colorSecondary cursorPointer support-tour">
              Contact Support
            </span>
          </p>
        </div>
      </div>

      {showTutorial && (
        <TutorialVideoModal
          videoData={showTutorial}
          closeModal={() => setShowTutorial(null)}
        />
      )}
    </PromptWrapper>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user,
  };
};

export default connect(mapStateToProps)(WelcomeSearchPrompt);
